import React, { useState } from 'react';
import { Modal, Form, Alert, Spin, Tooltip } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { ENTITY_REQUEST_ENTITY_TYPES } from '../../types/entity_add_requests';
import useMountEffect from '../../hooks/useMountEffect';
import OWButton from '../ow_button/OWButton';
import SupplierContactFields from './SupplierContactFields';
import SupplierCoverageFields from './SupplierCoverageFields';
import EntityRequestAnswerFields from '../entity_request_form/EntityRequestAnswerFields';
import {
	buildSupplierPayload,
	buildAnswers,
	getSupplierRequestInitialValues,
	formatMatchNames,
} from './supplierRequestFormUtils';
import {
	entityAddRequestsRestCrudThunksForBuyer,
	submitEntityAddRequest,
} from '../../thunks/entity_add_requests_thunks';
import { fetchSuppliersForBuyers } from '../../thunks/supplier_private_networks_thunks';
import { fetchActiveEntityRequestForm } from '../../thunks/entity_request_forms_thunks';
import {
	problemTypesHeirarchicalForBuyer,
	problemTypesRestCrudThunksForBuyer,
} from '../../thunks/problem_types_thunks';
import {
	fetchLocationsWithRegionsForBuyer,
	locationsRestCrudThunksForBuyer,
} from '../../thunks/locations_thunks';

const CONTACT_STEP = 0;
const COVERAGE_STEP = 1;
const QUESTIONS_STEP = 2;
const LAST_STEP = QUESTIONS_STEP;

const CONTACT_STEP_FIELDS = ['companyName', 'contactEmail', 'contactFirstName', 'contactLastName'];

// Redux collection the reused supplier search dispatches into; we only consume the resolved
// matches here, so the exact name is irrelevant beyond keeping it scoped to this form.
const DUPLICATE_SEARCH_COLLECTION = 'requestToAddSupplierDuplicateSearch';

function RequestToAddSupplierForm({
	visible,
	record,
	hideModal,
	onSuccess,
	problemTypes,
	locations,
	fetchProblemTypes,
	fetchMultipleProblemTypes,
	fetchLocations,
	fetchMultipleLocations,
	fetchActiveForm,
	createRequest,
	updateRequest,
	submitRequest,
	searchSuppliers,
}) {
	const [form] = Form.useForm();
	const [step, setStep] = useState(CONTACT_STEP);
	const [questions, setQuestions] = useState([]);
	const [questionsLoading, setQuestionsLoading] = useState(true);
	const [savingDraft, setSavingDraft] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [errorMessage, setErrorMessage] = useState(undefined);
	// Duplicate pre-check: `matches` holds any existing suppliers found for the entered name+email;
	// `warnedKey` records the exact name|email we last warned for, so a second Next click on the same
	// values proceeds, while changing either value re-runs the check.
	const [duplicateMatches, setDuplicateMatches] = useState([]);
	const [duplicateWarnedKey, setDuplicateWarnedKey] = useState(undefined);
	const [checkingDuplicates, setCheckingDuplicates] = useState(false);

	// Seeded from an existing record; also set after a successful create so that a submit failure
	// followed by a retry updates the same draft instead of creating a duplicate request.
	const [persistedId, setPersistedId] = useState(nullSafeGet('id', record));
	// A real load failure (network / permission) for the active request form — distinct from the
	// resolved-null "no active form" case. Blocks submission so required questions can't be skipped.
	const [formLoadFailed, setFormLoadFailed] = useState(false);

	useMountEffect(() => {
		fetchActiveForm(ENTITY_REQUEST_ENTITY_TYPES.supplier)
			.then((activeForm) => {
				setQuestions(activeForm ? nullSafeGet('questions', activeForm) || [] : []);
			})
			.catch(() => {
				setFormLoadFailed(true);
			})
			.finally(() => {
				setQuestionsLoading(false);
			});
	});

	const persist = (values) => {
		const payload = buildSupplierPayload(values);
		const answers = buildAnswers(questions, values);
		const entity = {
			entityType: ENTITY_REQUEST_ENTITY_TYPES.supplier,
			payload,
			answers,
		};
		const request = persistedId
			? updateRequest({ ...entity, id: persistedId })
			: createRequest(entity);
		return request.then((saved) => {
			const savedId = nullSafeGet('id', saved);
			if (savedId) {
				setPersistedId(savedId);
			}
			return saved;
		});
	};

	const handleNext = () => {
		setErrorMessage(undefined);
		if (step === CONTACT_STEP) {
			form
				.validateFields(CONTACT_STEP_FIELDS)
				.then(() => {
					const values = form.getFieldsValue(true);
					const searchKey = `${values.companyName}|${values.contactEmail}`;
					// Already warned for these exact values → the user chose to continue anyway.
					if (duplicateWarnedKey === searchKey) {
						setStep(COVERAGE_STEP);
						return;
					}
					setCheckingDuplicates(true);
					searchSuppliers(values.companyName, values.contactEmail)
						.then((matches) => {
							setCheckingDuplicates(false);
							if (matches && matches.length > 0) {
								// Non-blocking warning: surface possible existing suppliers and let the user
								// click Next again to proceed and request a duplicate if they choose to.
								setDuplicateMatches(matches);
								setDuplicateWarnedKey(searchKey);
							} else {
								setDuplicateMatches([]);
								setDuplicateWarnedKey(undefined);
								setStep(COVERAGE_STEP);
							}
						})
						.catch(() => {
							// A failed lookup must never block the request — just advance.
							setCheckingDuplicates(false);
							setStep(COVERAGE_STEP);
						});
				})
				.catch(() => {});
			return;
		}
		setStep(step + 1);
	};

	const handleSaveDraft = () => {
		setErrorMessage(undefined);
		setSavingDraft(true);
		const values = form.getFieldsValue(true);
		persist(values)
			.then(() => {
				setSavingDraft(false);
				onSuccess();
				hideModal();
			})
			.catch((error) => {
				setSavingDraft(false);
				setErrorMessage(
					typeof error === 'string' ? error : 'Something went wrong saving the draft.'
				);
			});
	};

	// Explicit final-step submit. We validate the whole form ourselves rather than using a native
	// submit button so advancing steps can never trigger an accidental submit.
	const handleSendRequest = () => {
		setErrorMessage(undefined);
		form
			.validateFields()
			.then((values) => {
				setSubmitting(true);
				return persist(values)
					.then((saved) => submitRequest(nullSafeGet('id', saved)))
					.then(() => {
						setSubmitting(false);
						onSuccess();
						hideModal();
					})
					.catch((error) => {
						setSubmitting(false);
						setErrorMessage(
							typeof error === 'string' ? error : 'Something went wrong submitting this request.'
						);
					});
			})
			.catch(() => {
				// Validation errors are surfaced inline on the questions step.
			});
	};

	const modalTitle = (
		<div>
			<div className="flex flex-row items-start justify-between">
				<div className="h5medium text-character-primary">Request to add a Supplier</div>
			</div>
			<div className="footnoteregular mt-1 flex flex-row items-center justify-between text-character-secondary">
				<span>Request a supplier your team works with that isn't on OpenWrench yet.</span>
				<Tooltip title="Approvers will be notified to review your request">
					<span
						className="flex flex-row items-center text-character-secondary"
						style={{ gap: '4px' }}
					>
						<TeamOutlined />
						Approvers
					</span>
				</Tooltip>
			</div>
		</div>
	);

	// The form fields only mount once questions have loaded; block persist/submit/advance actions
	// until then (and on a load failure) so nothing runs against unmounted/undefined fields. Also lock
	// out every footer action while any one is in flight: for a new request Save Draft and Send Request
	// both call persist() before persistedId is set, so leaving the others enabled lets two creates race
	// into a duplicate draft/submission. Each button still shows its own spinner via its `loading` prop.
	const actionInFlight = savingDraft || submitting || checkingDuplicates;
	const actionsDisabled = questionsLoading || formLoadFailed || actionInFlight;

	const footer = (
		<div className="flex flex-row items-center justify-between">
			<OWButton
				type="default"
				onClick={handleSaveDraft}
				loading={savingDraft}
				disabled={actionsDisabled}
			>
				Save Draft
			</OWButton>
			<div className="flex flex-row items-center" style={{ gap: '8px' }}>
				<OWButton type="default" onClick={hideModal}>
					Cancel
				</OWButton>
				{step < LAST_STEP ? (
					<OWButton
						type="primary"
						htmlType="button"
						onClick={handleNext}
						loading={checkingDuplicates}
						disabled={actionsDisabled}
					>
						Next
					</OWButton>
				) : (
					<OWButton
						type="primary"
						htmlType="button"
						onClick={handleSendRequest}
						loading={submitting}
						disabled={actionsDisabled}
					>
						Send Request
					</OWButton>
				)}
			</div>
		</div>
	);

	return (
		<Modal
			visible={visible}
			width={600}
			title={modalTitle}
			onCancel={hideModal}
			closable={true}
			destroyOnClose={true}
			footer={footer}
		>
			{questionsLoading ? (
				<div className="flex w-full justify-center py-8">
					<Spin />
				</div>
			) : formLoadFailed ? (
				<Alert
					type="error"
					showIcon
					message="Couldn't load the request form"
					description="Please close and try again — this request can't be submitted until the form loads."
				/>
			) : (
				<Form
					form={form}
					layout="vertical"
					initialValues={getSupplierRequestInitialValues(record, questions)}
				>
					{errorMessage ? <Alert message={errorMessage} type="error" className="mb-4" /> : null}
					{step === CONTACT_STEP && duplicateMatches.length > 0 ? (
						<Alert
							type="warning"
							className="mb-4"
							showIcon
							message="This supplier may already exist"
							description={`A similar supplier is already on OpenWrench: ${formatMatchNames(
								duplicateMatches
							)}. Click Next again to request adding it anyway.`}
						/>
					) : null}
					{/* All steps stay mounted (hidden) so values + "Select All" watches persist across steps. */}
					<div hidden={step !== CONTACT_STEP}>
						<SupplierContactFields />
					</div>
					<div hidden={step !== COVERAGE_STEP}>
						<SupplierCoverageFields
							form={form}
							problemTypes={problemTypes}
							fetchProblemTypes={fetchProblemTypes}
							fetchMultipleProblemTypes={fetchMultipleProblemTypes}
							locations={locations}
							fetchLocations={fetchLocations}
							fetchMultipleLocations={fetchMultipleLocations}
						/>
					</div>
					<div hidden={step !== QUESTIONS_STEP}>
						<EntityRequestAnswerFields questions={questions} />
					</div>
				</Form>
			)}
		</Modal>
	);
}

const mapStateToProps = (state) => ({
	problemTypes: state.problem_types,
	locations: state.locations,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch) => ({
	fetchActiveForm: (entityType) =>
		dispatch(fetchActiveEntityRequestForm(ROLE_TYPES.BUYER)(entityType)),
	createRequest: (entity) => dispatch(entityAddRequestsRestCrudThunksForBuyer.create(entity)),
	updateRequest: (entity) => dispatch(entityAddRequestsRestCrudThunksForBuyer.update(entity)),
	submitRequest: (id) => dispatch(submitEntityAddRequest(ROLE_TYPES.BUYER)(id)),
	searchSuppliers: (name, email) =>
		dispatch(fetchSuppliersForBuyers(ROLE_TYPES.BUYER)(name, email)(DUPLICATE_SEARCH_COLLECTION)),
	fetchProblemTypes: (params, targetCollectionName, pagination, sorting, filters, addTo) =>
		dispatch(
			problemTypesHeirarchicalForBuyer(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addTo
			)
		),
	fetchMultipleProblemTypes: (ids, targetCollectionName) =>
		dispatch(problemTypesRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)),
	fetchLocations: (params, targetCollectionName, pagination, sorting, filters, addTo) =>
		dispatch(
			fetchLocationsWithRegionsForBuyer(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addTo
			)
		),
	fetchMultipleLocations: (ids, targetCollectionName) =>
		dispatch(locationsRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RequestToAddSupplierForm);
